import {checkRequiredFields} from './additionalFields';
import {type WCCartItem} from '../../../@type/woocommerce/cart-item';
import {store} from '../store';
import {Carts, DefaultCart, updateCartPackageShippingMethod} from '../reducers/cartReducer';
import {Environment, Feature, updateEnvironment} from '../reducers/environmentReducer';
import {MerchantConfiguration, updateMerchantGeneralConfig, updateMerchantShippingConfig} from '../reducers/merchantConfigurationReducer';
import {startModalLoading, stopModalLoading} from '../reducers/environmentReducer';
import {PeachPayOrder, setOrderError, updateCustomerAddressValidation} from '../reducers/peachPayOrderReducer';
import {type ICartCalculationRecord, type ICartMetaData, type IShippingMethod, type IShippingPackage} from '../../../@type/woocommerce/cart-calculation';
import {formatCurrencyString} from '../util/currency';
import {cartIsVirtual} from '../util/cart';
import {requestCartCalculation} from '../payment/order';
import {buildSubscriptionPriceMetaData} from '../util/subscription';
import {syncCustomerFieldChanges} from './customer';
import {getLocaleText} from '../util/translation';
import {collectCustomerFieldData, getFullAddress, getFullName} from './customer';
import {$qs, $qsAll} from '../../../@shared/ts/dom';
import {type InitMessage} from '../../../@type/window-messages';
import {FeatureFlag} from '../../../@type/features';
import {captureSentryException} from '../../../@shared/ts/sentry';
import {maybeFetch} from '../../../@shared/ts/maybe';

export function initShipping(message: InitMessage) {
	initShippingEvents();
	store.dispatch(updateMerchantGeneralConfig({
		...store.getState().merchantConfiguration.general,
		wcLocationInfoData: message.phpData.wc_location_info,
	}));

	store.dispatch(updateMerchantShippingConfig({
		shippingZones: Number.parseInt(message.phpData.num_shipping_zones),
	}));
}

function initShippingEvents() {
	store.subscribe(renderShipping);

	$qs('#pp-shipping-options')?.addEventListener('change', handleShippingSelectionEvent);

	$qs('#pp-refresh-shipping-options')?.addEventListener('click', async () => {
		store.dispatch(startModalLoading());
		let isValidAddress = true;
		if (!Carts.virtual() || !Feature.enabled(FeatureFlag.VIRTUAL_PRODUCT_FIELDS)) {
			isValidAddress = await validateAddress();
		}

		await requestCartCalculation();
		store.dispatch(updateCustomerAddressValidation(isValidAddress));
		store.dispatch(stopModalLoading());
		if (isValidAddress) {
			$qs('#pp-refresh-shipping-options', $element => {
				$element.style.display = 'none';
			});
			$qs<HTMLInputElement>(
				'#pp-continue-to-payment',
				$element => {
					$element.disabled = false;
				},
			);
		}
	});

	$qs('#pp-info-form')?.addEventListener('submit', async (event: Event) => {
		event.preventDefault();
		store.dispatch(startModalLoading());
		store.dispatch(setOrderError(''));
		setTimeout(() => {
			syncCustomerFieldChanges('#pp-info-form');
		});
		let isValidAddress = true;
		if (!Carts.virtual() || !Feature.enabled(FeatureFlag.VIRTUAL_PRODUCT_FIELDS)) {
			isValidAddress = await validateAddress();
		}

		await requestCartCalculation();
		store.dispatch(updateCustomerAddressValidation(isValidAddress));
		if (isValidAddress && !PeachPayOrder.errorMessage() && checkRequiredFields()) {
			store.dispatch(updateEnvironment({
				modalPageType: Environment.modalUI.requestedPage(),
				requestedPage: undefined,
			}));
		} else {
			store.dispatch(updateEnvironment({
				requestedPage: undefined,
			}));
		}

		store.dispatch(stopModalLoading());
	});
}

/**
 * Window fetch for validating the billing address.
 */
export async function validateAddress(): Promise<boolean> {
	const validateURL = Feature.metadata<string>(FeatureFlag.EXPRESS_CHECKOUT, 'validate_url');
	if (!validateURL) {
		throw new Error('Validate URL is not set');
	}

	const wcBillingFields = [
		'billing_first_name',
		'billing_last_name',
		'billing_company',
		'billing_email',
		'billing_phone',
		'billing_country',
		'billing_address_1',
		'billing_address_2',
		'billing_city',
		'billing_state',
		'billing_postcode',
	];

	const formData = new FormData();
	Object.entries(collectCustomerFieldData('#pp-info-form', wcBillingFields) ?? {}).forEach(([key, value]) => {
		formData.append(key, value);
	});
	formData.append('ship_to_different_address', 'false');

	const {error, result} = await maybeFetch<{success: boolean; error_messages: string[]}>(validateURL, {
		method: 'POST',
		body: formData,
	});

	if (error || !result) {
		captureSentryException(error instanceof Error ? error : new Error('Unknown error while validating billing address'));
		return false;
	}

	if (!result.success) {
		// eslint-disable-next-line no-alert
		alert(result.error_messages.join('\n').replace(/(<([^>]+)>)/gi, ''));
		return false;
	}

	return true;
}

async function handleShippingSelectionEvent(event: Event) {
	const $target = event.target as HTMLInputElement;
	const $targetContainer = $target.closest<HTMLElement>('[data-cart-key]');

	const shippingMethodId = $target.value ?? '';
	const cartKey = $targetContainer?.dataset?.['cartKey'] ?? '';
	const packageKey = $targetContainer?.dataset?.['packageKey'] ?? '';

	store.dispatch(updateCartPackageShippingMethod({
		cartKey,
		shippingPackageKey: packageKey,
		packageMethodId: shippingMethodId,
	}));

	// Update the tax and other values that may change with different shipping cost.
	store.dispatch(startModalLoading());

	await requestCartCalculation();

	store.dispatch(stopModalLoading());
}

/**
 * Called whenever shipping rendering needs to be updated.
 */
function renderShipping() {
	renderOrderHeader(DefaultCart.contents());

	// Render shipping options
	if (cartIsVirtual(DefaultCart.contents())) {
		$qs('#pp-checkout-status-middle-text+span')?.classList.add('hide');
		$qs('#pp-checkout-status-middle-text+span+span')?.classList.remove('hide');
	} else {
		$qs('#pp-checkout-status-middle-text+span')?.classList.remove('hide');
		$qs('#pp-checkout-status-middle-text+span+span')?.classList.add('hide');
		renderCartShippingOptions(store.getState().calculatedCarts);
	}
}

/**
 * Sets up the modal based on if the cart is completely virtual or not
 */
function renderOrderHeader(cart: WCCartItem[]) {
	if (cartIsVirtual(cart)) {
		$qs('.shipping-address-header')?.classList.add('hide');
		$qs('.billing-address-header')?.classList.remove('hide');

		for (const $element of $qsAll('.hide-for-virtual-carts')) {
			$element.classList.add('hide');
		}

		for (const $element of $qsAll('.show-for-virtual-carts')) {
			if (getFullName('shipping') === '' && getFullAddress('shipping').replace(/\s/g, '') === '') {
				return;
			}

			$element.classList.remove('hide');
		}
	} else {
		$qs('.shipping-address-header')?.classList.remove('hide');
		$qs('.billing-address-header')?.classList.add('hide');

		for (const $element of $qsAll('.hide-for-virtual-carts')) {
			$element.classList.remove('hide');
		}

		for (const $element of $qsAll('.show-for-virtual-carts')) {
			$element.classList.add('hide');
		}
	}
}

/**
 * Renders all possible shipping options based on all calculated carts
 */
function renderCartShippingOptions(calculatedCarts: ICartCalculationRecord) {
	let shippingOptionsHTML = '';
	for (const [cartKey, cartCalculation] of Object.entries(calculatedCarts)) {
		if (!cartCalculation) {
			continue;
		}

		for (const [shippingPackageKey, shippingPackage] of Object.entries(cartCalculation.package_record)) {
			if (!shippingPackage) {
				continue;
			}

			shippingOptionsHTML += renderShippingPackageOptions(cartKey, shippingPackageKey, shippingPackage, cartCalculation.cart_meta, Object.entries(calculatedCarts).length > 1);
		}
	}

	// New customer
	$qs('#pp-shipping-options', $element => {
		$element.innerHTML = shippingOptionsHTML;
	});
}

/**
 * Builds the HTML needed for a single package shipping options.
 */
// eslint-disable-next-line max-params
function renderShippingPackageOptions(cartKey: string, shippingPackageKey: string, shippingPackage: IShippingPackage, cartMeta: ICartMetaData, shouldShowPackageName: boolean): string {
	const methodOptionBuilder = (methodKey: string, method: IShippingMethod, selected: boolean) => `
<div class="pp-disabled-loading pp-radio-line pp-shipping-option-row${selected ? ' fill' : ''}" for="shipping_method_${shippingPackageKey}_${methodKey.replace(/:/g, '')}">
	<div class="pp-shipping-option-bg"></div>
	<input class="pp-disabled-loading" id="shipping_method_${shippingPackageKey}_${methodKey.replace(/:/g, '')}" name="shipping_method[${shippingPackageKey}]" value="${methodKey}" type="radio" ${
	selected ? 'checked' : ''
} required>
	${
	method.description
		? `<div class="flex col w-100">
				<label for="shipping_method_${shippingPackageKey}_${methodKey.replace(/:/g, '')}">
					<span>${method.title}</span>
					<span class="shipping-price pp-currency-blur">
						${formatCurrencyString(method.total)}
						<span class="muted">${buildSubscriptionPriceMetaData(cartMeta)}</span>
					</span>
				</label>
			<div>${method.description}</div>
		</div>`
		: `<label style="width: 100%;" for="shipping_method_${shippingPackageKey}_${methodKey.replace(/:/g, '')}">
				<span>${method.title}</span> <span class="shipping-price pp-currency-blur">${formatCurrencyString(method.total)}
				<span class="muted">${buildSubscriptionPriceMetaData(cartMeta)}</span></span>
				</label>`
}
</div>`;

	/**
	 * Since the package name string is being displayed in the modal but coming from Woocommerce, we need to hardcode these strings' values
	 * so they can be captured by the gettext parser and be given translations.
	 */
	const packageNameTranslations: Record<string, string> = {
		Shipping: getLocaleText('Shipping'),
		'Initial Shipment': getLocaleText('Initial Shipment'),
		'Recurring Shipment': getLocaleText('Recurring Shipment'),
	};
	const packageName = packageNameTranslations[shippingPackage.package_name] ?? shippingPackage.package_name;
	const packageNameHTML = `<div class="pp-title">${packageName}</div>`;

	const packageMethodOptionsHTML: string = Object.entries(shippingPackage.methods).map(([shippingMethodKey, shippingMethod]) =>
		shippingMethod ? methodOptionBuilder(shippingMethodKey, shippingMethod, shippingPackage.selected_method === shippingMethodKey) : '',
	).join('');

	return `${shouldShowPackageName ? packageNameHTML : ''}
	<div class="pp-shipping-options-container" data-cart-key="${cartKey}" data-package-key="${shippingPackageKey}">
	${packageMethodOptionsHTML}
	</div>`;
}

/**
 * Determines if the shipping which may be validated is in a zone that is able to be shipped to.
 */
export function shippingIsValid(): boolean {
	// If cart is virtual then it does not require shipping.
	if (cartIsVirtual(DefaultCart.contents())) {
		return true;
	}

	// If no shipping zones then it means shipping can be anywhere
	if (MerchantConfiguration.shipping.shippingZones() === 0) {
		return true;
	}

	// If no options are available then the address will not work.
	if (!Carts.anyShippingMethodsAvailable()) {
		return false;
	}

	return true;
}
